import { useEffect } from "react";
import { Controller } from "react-hook-form";
import {
  H1,
  P,
  InputRHFV2,
  SelectRHFV2,
  InfoV2,
  ContainerV2,
  MessageErroFormV2,
} from "../../Atoms";
import { Tracking } from "../../../services/Tracking";
import { maskCep, maskPhone } from "../../../utils/masks";
import { useChangeServiceAddress } from "./useChangeServiceAddress";

export const ChangeServiceAddress = () => {
  const {
    register,
    control,
    handleSubmit,
    errors,
    watch,
    ufOptions,
    cidadeOptions,
    handleCepBlur,
    handleUfChange,
    isSubmitting,
  } = useChangeServiceAddress();

  useEffect(() => {
    Tracking.pageView("mapa:agendamento/alterar-endereco:pageview");
  }, []);

  const { onChange: onTelefoneChange, ...telefoneField } = register("telefone");
  const {
    onChange: onCepChange,
    onBlur: onCepBlur,
    ...cepField
  } = register("endereco.cep");

  return (
    <>
      <ContainerV2
        backButton={{ className: "h-[38px]" }}
        nextButton={{
          title: "Avançar",
          disabled: isSubmitting,
          type: "submit",
          form: "change-service-address-form",
          className: "h-[38px]",
        }}
      >
        <H1 className="mb-4 mt-8">Alterar Endereço</H1>
        <P className="mb-8 text-[#666666]">
          Edite o endereço de realização do serviço.
        </P>

        <form
          id="change-service-address-form"
          onSubmit={handleSubmit}
          className="flex flex-col gap-6 rounded-[24px] border border-solid border-[#e5e5ea] bg-white p-10 shadow-[0_8px_24px_0_rgba(0,0,0,0.078)]"
        >
          <div className="relative w-full pb-4">
            <InputRHFV2
              id="nome"
              label="Nome"
              required
              placeholder="Digite seu nome completo"
              {...register("nome")}
            />
            {errors.nome?.message && (
              <MessageErroFormV2 className="top-full mt-1">
                {errors.nome.message}
              </MessageErroFormV2>
            )}
          </div>

          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="relative w-full pb-4">
              <InputRHFV2
                id="telefone"
                label="Telefone"
                required
                placeholder="(00) 0000-0000"
                {...telefoneField}
                onChange={(e) => {
                  e.target.value = maskPhone(e.target.value);
                  onTelefoneChange(e);
                }}
              />
              {errors.telefone?.message && (
                <MessageErroFormV2 className="top-full mt-1">
                  {errors.telefone.message}
                </MessageErroFormV2>
              )}
            </div>

            <div className="relative w-full pb-4">
              <InputRHFV2
                id="cep"
                label="CEP"
                required
                placeholder="00000-000"
                {...cepField}
                onChange={(e) => {
                  e.target.value = maskCep(e.target.value);
                  onCepChange(e);
                }}
                onBlur={(e) => {
                  onCepBlur(e);
                  handleCepBlur(e.target.value);
                }}
              />
              {errors.endereco?.cep?.message && (
                <MessageErroFormV2 className="top-full mt-1">
                  {errors.endereco.cep.message}
                </MessageErroFormV2>
              )}
            </div>
          </div>

          <div className="relative w-full pb-4">
            <InputRHFV2
              id="logradouro"
              label="Logradouro"
              required
              placeholder="Digite o logradouro"
              {...register("endereco.logradouro")}
            />
            {errors.endereco?.logradouro?.message && (
              <MessageErroFormV2 className="top-full mt-1">
                {errors.endereco.logradouro.message}
              </MessageErroFormV2>
            )}
          </div>

          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="relative w-full pb-4">
              <InputRHFV2
                id="numero"
                label="Número"
                required
                {...register("endereco.numero")}
              />
              {errors.endereco?.numero?.message && (
                <MessageErroFormV2 className="top-full mt-1">
                  {errors.endereco.numero.message}
                </MessageErroFormV2>
              )}
            </div>

            <div className="relative w-full pb-4">
              <InputRHFV2
                id="complemento"
                label="Complemento"
                {...register("endereco.complemento")}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 md:flex-row">
            <div className="relative w-full pb-4">
              <InputRHFV2
                id="bairro"
                label="Bairro"
                required
                {...register("endereco.bairro")}
              />
              {errors.endereco?.bairro?.message && (
                <MessageErroFormV2 className="top-full mt-1">
                  {errors.endereco.bairro.message}
                </MessageErroFormV2>
              )}
            </div>

            <div className="relative w-full pb-4">
              <Controller
                control={control}
                name="endereco.uf"
                render={({ field }) => (
                  <SelectRHFV2
                    id="uf"
                    label="UF"
                    required
                    placeholder="Selecione"
                    options={ufOptions}
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      field.onChange(e);
                      handleUfChange();
                    }}
                  />
                )}
              />
              {errors.endereco?.uf?.message && (
                <MessageErroFormV2 className="top-full mt-1">
                  {errors.endereco.uf.message}
                </MessageErroFormV2>
              )}
            </div>

            <div className="relative w-full pb-4">
              <Controller
                control={control}
                name="endereco.cidade"
                render={({ field }) => (
                  <SelectRHFV2
                    id="cidade"
                    label="Cidade"
                    required
                    placeholder="Selecione"
                    disabled={!watch("endereco.uf")}
                    options={cidadeOptions}
                    {...field}
                    value={field.value ?? ""}
                  />
                )}
              />
              {errors.endereco?.cidade?.message && (
                <MessageErroFormV2 className="top-full mt-1">
                  {errors.endereco.cidade.message}
                </MessageErroFormV2>
              )}
            </div>
          </div>
        </form>
      </ContainerV2>

      <InfoV2 className="w-full max-w-7xl mx-auto" required>
        O local de realização do agendamento é de responsabilidade o cliente.
        Evite vias públicas!
      </InfoV2>
    </>
  );
};
